/** CSV exporters — ported from the mobile CsvExporter (same columns/format). */
import type { FuelLog, Spending, Trip } from "./model.ts";

/** Quote fields containing separators and neutralize spreadsheet formula injection. */
function csvField(value: string): string {
  const safe = value.length > 0 && "=+-@\t\r".includes(value[0]!) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function isoDate(epoch: number | null | undefined): string {
  if (epoch == null) return "";
  return new Date(epoch * 1000).toISOString().slice(0, 10);
}
const n = (v: number | null | undefined, dp = 2): string => (v == null ? "" : v.toFixed(dp));

export function fuelLogsToCsv(logs: FuelLog[], metric: boolean): string {
  const distUnit = metric ? "km" : "mi", volUnit = metric ? "L" : "gal";
  const f = metric ? 1 : 0.621371, vf = metric ? 1 : 1 / 3.785411784;
  let sb = `Date,Odometer (${distUnit}),Volume (${volUnit}),Energy (kWh),Fuel Unit Price (per ${volUnit}),Charge Unit Price (per kWh),Total Cost,Currency,Station,Notes\n`;
  for (const l of [...logs].sort((a, b) => (b.date ?? 0) - (a.date ?? 0))) {
    const fuelUnitPrice = l.volumeAdded > 0 ? l.totalCost / l.volumeAdded : null;
    const chargeUnitPrice = (l.kwhAdded ?? 0) > 0 ? l.totalCost / (l.kwhAdded ?? 1) : null;
    sb += [isoDate(l.date), n(l.odometer * f, 0), n(l.volumeAdded * vf), l.kwhAdded != null ? n(l.kwhAdded) : "",
      fuelUnitPrice != null ? n(fuelUnitPrice / vf) : "", chargeUnitPrice != null ? n(chargeUnitPrice) : "",
      n(l.totalCost), l.currency, l.stationName ?? "", l.notes ?? ""].map((x) => csvField(String(x))).join(",") + "\n";
  }
  return sb;
}

export function spendingsToCsv(spendings: Spending[]): string {
  let sb = "Date,Amount,Category,Currency,Notes\n";
  for (const s of [...spendings].sort((a, b) => (b.date ?? 0) - (a.date ?? 0))) {
    sb += [isoDate(s.date), n(s.amount), s.category ?? "", s.currency, s.notes ?? ""].map((x) => csvField(String(x))).join(",") + "\n";
  }
  return sb;
}

export function tripsToCsv(trips: Trip[], metric: boolean): string {
  const distUnit = metric ? "km" : "mi", volUnit = metric ? "L" : "gal";
  const f = metric ? 1 : 0.621371, vf = metric ? 1 : 1 / 3.785411784;
  const speedUnit = metric ? "km/h" : "mph", sf = metric ? 1 : 0.621371;
  let sb = `Date,Start,End,Distance (${distUnit}),Fuel Volume (${volUnit}),Fuel Energy (kWh),Fuel Cost,Toll Cost,Parking Cost,Total Cost,Purpose,Duration (min),Avg Speed (${speedUnit}),Top Speed (${speedUnit}),Waypoints,Notes\n`;
  for (const t of [...trips].sort((a, b) => (b.date ?? 0) - (a.date ?? 0))) {
    const dist = Math.max(0, t.endOdometer - t.startOdometer);
    const total = (t.fuelCost ?? 0) + (t.tollCost ?? 0) + (t.parkingCost ?? 0);
    sb += [isoDate(t.date), t.startLocationName ?? "", t.endLocationName ?? "", n(dist * f, 0),
      t.fuelVolume != null ? n(t.fuelVolume * vf) : "", t.fuelEnergy != null ? n(t.fuelEnergy) : "",
      n(t.fuelCost), n(t.tollCost), n(t.parkingCost), n(total), t.purpose ?? "",
      t.durationSec != null ? n(t.durationSec / 60, 0) : "",
      t.avgSpeedKmh != null ? n(t.avgSpeedKmh * sf, 0) : "",
      t.topSpeedKmh != null ? n(t.topSpeedKmh * sf, 0) : "",
      (t.waypoints ?? []).join(" | "), t.notes ?? ""]
      .map((x) => csvField(String(x))).join(",") + "\n";
  }
  return sb;
}
